import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Sparkles, X } from "lucide-react";
import { fetchUsage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useReaderAppMode } from "../lib/readerAppMode";
import { safeSession } from "../lib/safeStorage";

/**
 * Persistent "you've used your free tutorials" banner.
 *
 * Shows only when:
 *   • User is authenticated (guests don't have a meaningful quota)
 *   • They're on the Free tier (Pro has unlimited, no banner needed)
 *   • `tutorials_remaining === 0` (used all 3 free tutorials)
 *
 * Compliance notes:
 *   • Web build: banner includes an Upgrade CTA to `/pricing`
 *   • iOS Reader-app build: banner stays but omits the Upgrade CTA
 *     and only points users to their account — no prices, no external
 *     links (Apple Guideline 3.1.3).
 *
 * The dismiss state persists in sessionStorage so the banner doesn't
 * immediately re-appear on every page navigation — but it DOES return
 * on a fresh session so the user is reminded on their next visit.
 */

const DISMISS_KEY = "trubeaute.limit_banner_dismissed_at";
// Phase 2 (Feb 27 2026): the dismiss window was 6h — too short. A
// returning user shouldn't get re-prompted within the same day. Raise
// to 24h so the banner appears at most once per calendar day.
const REMINDER_HOURS = 24;

const LimitBanner = () => {
  const { user } = useAuth();
  const readerApp = useReaderAppMode();
  const [usage, setUsage] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    const raw = safeSession.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    const ageHours = (Date.now() - ts) / 3_600_000;
    return ageHours < REMINDER_HOURS;
  });

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    // Fetch usage on mount + whenever the user changes. The banner
    // will re-render if the API says the limit has been reached.
    fetchUsage()
      .then((u) => {
        if (!cancelled) setUsage(u);
      })
      .catch(() => {
        /* silent — non-critical */
      });
    return () => {
      cancelled = true;
    };
    // We intentionally key on `user?.user_id` instead of the full `user`
    // object — the auth context replaces `user` on every refresh, which
    // would otherwise re-trigger this fetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      safeSession.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* no-op */
    }
  };

  // Gatekeeping: only render when all conditions line up.
  if (!user) return null;
  if (!usage) return null;
  if (usage.tier !== "free") return null;
  // Use the new tutorial-cap field (tutorials_remaining === 0 means
  // they've used all 3 free tutorials). Falls back to the legacy
  // monthly-routine field for old backend builds; either at 0 fires.
  const tutorialsExhausted = usage.tutorials_remaining === 0;
  const routinesExhausted = usage.routines_remaining === 0;
  if (!tutorialsExhausted && !routinesExhausted) return null;
  if (dismissed) return null;

  return (
    <div
      className="relative bg-gradient-to-r from-[#F4DCD3] via-[#F8E5DD] to-[#F4DCD3] border-b border-[#E5C5C2] px-4 sm:px-6 py-3"
      data-testid="limit-banner"
      role="status"
      aria-live="polite"
    >
      <div className="max-w-6xl mx-auto flex items-center gap-3">
        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-[#9B6A6C] text-white flex items-center justify-center">
          <AlertCircle className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-[#2C2123] font-medium leading-snug">
            You've used your{" "}
            {usage.tutorial_limit > 0 ? usage.tutorial_limit : 3} free
            tutorials.
          </p>
          <p className="text-xs text-[#5C4B51] mt-0.5">
            {readerApp
              ? "The full tutorial library lives in your TruBeauté account."
              : "Unlock the full library — every vibe, every product, on demand."}
          </p>
        </div>
        {!readerApp && (
          <Link
            to="/pricing"
            className="hidden sm:inline-flex items-center gap-1.5 bg-[#9B6A6C] text-white text-xs uppercase tracking-[0.18em] rounded-full px-4 py-2 hover:bg-[#805557] transition flex-shrink-0"
            data-testid="limit-banner-upgrade-cta"
          >
            <Sparkles className="h-3 w-3" />
            See plans
          </Link>
        )}
        <button
          onClick={handleDismiss}
          aria-label="Dismiss this banner"
          className="flex-shrink-0 text-[#9B6A6C] hover:text-[#2C2123] transition-colors p-2"
          data-testid="limit-banner-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default LimitBanner;
